import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./App.css";
import { getTransactionWindowCopy, getPreseasonWaiverCopy, getWaiverCustomizationNote } from "./utils/transactionWindowCopy";
import { WEEK_1_SCORING_START_LABEL } from "./utils/seasonCalendar";

const DEFAULT_TRANSACTION_WINDOW_COPY = getTransactionWindowCopy();
const PRESEASON_WAIVER_COPY = getPreseasonWaiverCopy();
const DEFAULT_WAIVER_CUSTOMIZATION_NOTE = getWaiverCustomizationNote();

export default function About({ onBack }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.hash === "#faq") {
      const el = document.getElementById("faq");
      if (el) {
        window.requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    }
  }, []);
  return (
    <div className="ffb-bg">
      <header className="ffb-header">
        <h1 className="ffb-header-title ffb-header-title-long">🏈 About Fantasy Football Classic™ 🏈</h1>
      </header>
      
      <div className="ffb-main-content">
        {/* Hero Section */}
        <div className="ffb-section">
          <div className="ffb-main-heading">The Fantasy Football Experience You've Never Had Before</div>
          <div className="ffb-body-text" style={{ fontSize: '1.2em', lineHeight: '1.6' }}>
            Welcome to Fantasy Football Classic™, where we've reimagined fantasy football by combining the thrill of competition with the rich history of the NFL. A full season-long fantasy football league — draft, weekly lineups, waivers, and playoffs — powered by real historical data from 1999–2025. This is not a one-game simulation or daily contest.
          </div>
        </div>

        {/* Value Proposition Section */}
        <div className="ffb-card">
          <h2 className="ffb-section-heading">Why Fantasy Football Classic™ is Different</h2>
          
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ color: '#2563eb', fontSize: '1.3em', marginBottom: 12 }}>🎯 Predictable Yet Unpredictable</h3>
            <div className="ffb-body-text">
              Every starter gets their own performance from actual historical NFL stats (1999–2025). When you save a lineup, each starter is assigned one historical game for that week — only from seasons when that player played. Scores reveal at each player&apos;s kickoff; your full lineup total decides the head-to-head matchup.
            </div>
          </div>

          <div style={{ marginBottom: 32 }}>
            <h3 style={{ color: '#2563eb', fontSize: '1.3em', marginBottom: 12 }}>📊 Data-Driven Strategy</h3>
            <div className="ffb-body-text">
              Make informed decisions based on comprehensive historical data. Only players with at least 10 games across two or more seasons in our historical database are eligible, so you draft from proven performers with enough track record to strategize.
            </div>
          </div>

          <div style={{ marginBottom: 32 }}>
            <h3 style={{ color: '#2563eb', fontSize: '1.3em', marginBottom: 12 }}>🔄 Ever-Evolving Experience</h3>
            <div className="ffb-body-text">
              Each year, we add the previous season's data to our database. This means your league experience grows richer over time, with more historical context and player options available for future drafts.
            </div>
          </div>

          <div style={{ marginBottom: 32 }}>
            <h3 style={{ color: '#2563eb', fontSize: '1.3em', marginBottom: 12 }}>🏆 Fair and Competitive</h3>
            <div className="ffb-body-text">
              Since all performances are based on real historical data, there's no advantage from insider information or lucky guesses about future performance. Success comes from understanding player history, strategic drafting, and smart roster management.
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="ffb-card">
          <h2 className="ffb-section-heading">How It Works</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, marginBottom: 24 }}>
            <div style={{ padding: 20, backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <h4 style={{ color: '#1e293b', marginBottom: 12 }}>1. Draft Your Team</h4>
              <div className="ffb-body-text">
                Select from a pool of real NFL players (QBs, RBs, WRs, TEs, Ks) who played between 1999-2025. Each player has their historical performance data available for analysis.
              </div>
            </div>
            
            <div style={{ padding: 20, backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <h4 style={{ color: '#1e293b', marginBottom: 12 }}>2. Set Your Lineup</h4>
              <div className="ffb-body-text">
                Waivers and trades: {PRESEASON_WAIVER_COPY} {DEFAULT_TRANSACTION_WINDOW_COPY} Bench players do not score.
              </div>
            </div>
            
            <div style={{ padding: 20, backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <h4 style={{ color: '#1e293b', marginBottom: 12 }}>3. Historical Performance</h4>
              <div className="ffb-body-text">
                Scores use real NFL stats for that NFL week number, drawn from random seasons (1999–2025). The league follows a fixed 2026 calendar: Week 1 scoring begins Thursday, Sept 10, 2026; 17 fantasy weeks (14 regular, playoffs 15–17).
              </div>
            </div>
            
            <div style={{ padding: 20, backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <h4 style={{ color: '#1e293b', marginBottom: 12 }}>4. Compete and Win</h4>
              <div className="ffb-body-text">
                Face off against other league members using standard fantasy scoring. The team with the most points each week wins, and the best overall record takes the championship.
              </div>
            </div>
          </div>
        </div>

        {/* Perfect For Section */}
        <div className="ffb-card">
          <h2 className="ffb-section-heading">Perfect For...</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ fontSize: '2em', marginBottom: 12 }}>🏈</div>
              <h4 style={{ color: '#1e293b', marginBottom: 8 }}>Football Fans</h4>
              <div className="ffb-body-text">
                Experience players from your favorite era and test your knowledge of NFL history.
              </div>
            </div>
            
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ fontSize: '2em', marginBottom: 12 }}>📈</div>
              <h4 style={{ color: '#1e293b', marginBottom: 8 }}>Data Enthusiasts</h4>
              <div className="ffb-body-text">
                Dive deep into historical statistics and use data analysis to build your championship team.
              </div>
            </div>
            
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ fontSize: '2em', marginBottom: 12 }}>🎮</div>
              <h4 style={{ color: '#1e293b', marginBottom: 8 }}>Fantasy Veterans</h4>
              <div className="ffb-body-text">
                Experience fantasy football in a completely new way with predictable yet exciting gameplay.
              </div>
            </div>
            
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ fontSize: '2em', marginBottom: 12 }}>👥</div>
              <h4 style={{ color: '#1e293b', marginBottom: 8 }}>Social Players</h4>
              <div className="ffb-body-text">
                Create private leagues with friends and family for a unique competitive experience.
              </div>
            </div>
          </div>
        </div>

        {/* From the Founder */}
        <div className="ffb-card">
          <h2 className="ffb-section-heading">From the Founder</h2>
          <div className="ffb-body-text" style={{ fontSize: '1.1em', lineHeight: '1.7' }}>
            <p style={{ marginBottom: 20 }}>
              Fantasy Football Classic™ was born from the desire to once again experience the nostalgia from the glory days of fantasy football seasons past.
            </p>
            
            <p style={{ marginBottom: 20 }}>
              My entry into fantasy football happened back when I was in high school in 2001 and a buddy invited me to play in a league with friends. (That same league is entering its 26th season in fall 2026, with many of those original participants still playing). I landed the first pick of the draft that year, but I had no idea what I was doing. Daunte Culpepper was projected to score the most points, so I went ahead and grabbed him with the first overall pick. He ended up injuring his knee in week 11 and needless to say I didn't finish too high in my inaugural season. Culpepper got bit by the injury bug, but I had been bit by the fantasy bug and I was hooked.
            </p>
            
            <p style={{ marginBottom: 20 }}>
              Injuries, player bye weeks, conflict of rooting for fantasy players who play against your favorite NFL team that week: these are regular parts of season-long fantasy leagues that many people lament. <strong>Fantasy Football Classic™ removes these issues.</strong>
            </p>
            
            <p style={{ marginBottom: 20 }}>
              Remember the big name players from seasons past? Marshall Faulk, LaDainian Tomlinson, Randy Moss, Calvin Johnson, Antonio Gates, Tony Gonzalez, Peyton Manning, and Tom Brady to name a few. Maybe you were lucky enough to have some of these stars on your teams, or maybe you missed out and wished you could have had them. <strong>Fantasy Football Classic™ gives you that chance!</strong>
            </p>
            
            <p style={{ fontStyle: 'italic', color: '#2563eb', fontWeight: 600 }}>
              I hope you&apos;ll give it a try — I&apos;d love to have you in the community.
            </p>
          </div>
        </div>

        {/* Full FAQ — linked from home page Quick answers */}
        <div className="ffb-card" id="faq">
          <h2 className="ffb-section-heading">FAQ — Rules, Pricing &amp; Calendar</h2>

          <h3 style={{ color: "#334155", fontSize: "1.05em", marginBottom: 8 }}>When should we draft?</h3>
          <div className="ffb-body-text" style={{ marginBottom: 20 }}>
            All leagues share the same NFL calendar. For a full 14-week regular season, finish your draft before Week 1 scoring begins ({WEEK_1_SCORING_START_LABEL}). Public leagues are scheduled to draft before Week 1; you cannot join a public league after its draft has started. Private leagues may draft later — scoring still follows the global calendar, so weeks before your draft will not have lineups or scores.
          </div>

          <h3 style={{ color: "#334155", fontSize: "1.05em", marginBottom: 8 }}>How does scoring work?</h3>
          <div className="ffb-body-text" style={{ marginBottom: 20 }}>
            Each week your starters get scores from real historical NFL games for that week number. Each starter draws their own game from a random season (1999–2025) when that player actually played — not one game shared by the whole team. When you save a lineup, each starter&apos;s game is rolled once and kept even if you swap them out and back in before kickoff. Scores reveal at that player&apos;s kickoff. Standard fantasy scoring applies; private leagues can customize rules.
          </div>

          <h3 style={{ color: "#334155", fontSize: "1.05em", marginBottom: 8 }}>Waivers after the draft (preseason)</h3>
          <div className="ffb-body-text" style={{ marginBottom: 20 }}>
            {PRESEASON_WAIVER_COPY}
          </div>

          <h3 style={{ color: "#334155", fontSize: "1.05em", marginBottom: 8 }}>Waivers during the season</h3>
          <div className="ffb-body-text" style={{ marginBottom: 20 }}>
            {DEFAULT_TRANSACTION_WINDOW_COPY} Traditional leagues use waiver priority during the claim-queue window; FAAB leagues use bidding during that same window and first come, first served (FCFS) pickups when the FCFS window is open. {DEFAULT_WAIVER_CUSTOMIZATION_NOTE}
          </div>

          <h3 style={{ color: "#334155", fontSize: "1.05em", marginBottom: 8 }}>Default roster &amp; scoring</h3>
          <div className="ffb-body-text" style={{ marginBottom: 20 }}>
            Default roster: 1 QB, 2 RB, 2 WR, 1 TE, 1 K, 1 FLEX (RB/WR/TE), plus 5 bench. Bench players do not score. Default scoring: 6 pts rushing/receiving TD, 4 pts passing TD, 1 pt per 10 rush/rec yards, 1 pt per 25 pass yards, −2 INT, −2 fumbles lost.
          </div>

          <h3 style={{ color: "#334155", fontSize: "1.05em", marginBottom: 8 }}>How much does it cost?</h3>
          <div className="ffb-body-text" style={{ marginBottom: 20 }}>
            Public leagues are <strong>free</strong>. Private leagues require a one-time platform fee paid by the commissioner ($40 inaugural season, 50% off). Members join private leagues free. Commissioners collect any league dues outside the platform. Payments through Stripe are for fantasyfootballclassic.com services only — not prize pools or member-to-member dues.
          </div>

          <h3 style={{ color: "#334155", fontSize: "1.05em", marginBottom: 8 }}>Can I play with friends?</h3>
          <div className="ffb-body-text" style={{ marginBottom: 20 }}>
            Yes — create a private league and invite friends, or use Quick Join for a free public league (up to 3 active public leagues per account). You can leave a public league before its draft to free a slot.
          </div>

          <h3 style={{ color: "#334155", fontSize: "1.05em", marginBottom: 8 }}>Community standards &amp; age</h3>
          <div className="ffb-body-text" style={{ marginBottom: 20 }}>
            Usernames, league names, team names, and draft chat must stay respectful; slurs and harassment are blocked. You must be at least 13 to create an account (COPPA).
          </div>

          <h3 style={{ color: "#334155", fontSize: "1.05em", marginBottom: 8 }}>Data attribution</h3>
          <div className="ffb-body-text" style={{ marginBottom: 0 }}>
            Historical NFL stats are provided by the open-source{" "}
            <a href="https://github.com/nflverse/nflverse" target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>nflverse project</a>{" "}
            (MIT License).
          </div>
        </div>

        {/* Call to Action */}
        <div className="ffb-card" style={{ textAlign: 'center', backgroundColor: '#dbeafe', border: '2px solid #2563eb' }}>
          <h2 className="ffb-section-heading" style={{ color: '#1e40af' }}>Ready to Relive Your Fantasy Football Glory Days?</h2>
          <div className="ffb-body-text" style={{ marginBottom: 24, fontSize: '1.1em' }}>
            Join the other players who have discovered the perfect blend of strategy, nostalgia, and competition. Start your Fantasy Football Classic™ journey today!
          </div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              className="ffb-btn" 
              onClick={onBack}
              style={{ backgroundColor: '#2563eb', color: 'white', fontSize: '1.1em', padding: '12px 24px' }}
            >
              Get Started
            </button>
            <button 
              className="ffb-btn" 
              onClick={() => navigate('/projections')}
              style={{ backgroundColor: '#059669', color: 'white', fontSize: '1.1em', padding: '12px 24px' }}
            >
              View Historical Player Stats
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer style={{ textAlign: "center", color: "#94a3b8", fontSize: "0.98em", marginTop: 40, padding: 20 }}>
          
          <div style={{ display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
            <button
              onClick={() => navigate("/terms")}
              style={{ 
                color: "#94a3b8", 
                textDecoration: "none",
                background: "none",
                border: "none",
                cursor: "pointer"
              }}
            >
              Terms of Service
            </button>
            <button
              onClick={() => navigate("/privacy")}
              style={{ 
                color: "#94a3b8", 
                textDecoration: "none",
                background: "none",
                border: "none",
                cursor: "pointer"
              }}
            >
              Privacy Policy
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
} 